import { PASSWORD_MIN_LENGTH } from '@cartly/shared';
import { Alert, Button, TextField } from '@cartly/ui';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api } from '../../api/client';
import { useSubmit, useUrlToken } from '../../app/hooks';
import { AuthLayout } from '../../layouts/AuthLayout';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const { pending, error, fieldErrors, run } = useSubmit();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await run(async () => {
      await api.post('/auth/password/forgot', { email });
      setSent(true);
    });
  };

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We'll email you a link to choose a new password."
      footer={<Link to="/login">Back to sign in</Link>}
    >
      {sent ? (
        <Alert tone="success" title="Check your inbox">
          If an account exists for {email}, a reset link is on its way. The link expires in 1 hour.
        </Alert>
      ) : (
        <form className="stack" onSubmit={onSubmit} noValidate>
          {error && <Alert tone="critical">{error}</Alert>}
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={fieldErrors['email']}
          />
          <Button type="submit" variant="primary" block loading={pending}>
            Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const token = useUrlToken();
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const { pending, error, fieldErrors, run } = useSubmit();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await run(async () => {
      await api.post('/auth/password/reset', { token, password });
      setDone(true);
    });
  };

  return (
    <AuthLayout title="Choose a new password" footer={<Link to="/login">Back to sign in</Link>}>
      {!token ? (
        <Alert tone="critical">This reset link is incomplete. Request a new one.</Alert>
      ) : done ? (
        <div className="stack">
          <Alert tone="success" title="Password updated">
            You were signed out everywhere. Sign in with your new password.
          </Alert>
          <Link to="/login" className="btn btn--primary btn--block">
            Sign in
          </Link>
        </div>
      ) : (
        <form className="stack" onSubmit={onSubmit} noValidate>
          {error && <Alert tone="critical">{error}</Alert>}
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            minLength={PASSWORD_MIN_LENGTH}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            hint={`At least ${PASSWORD_MIN_LENGTH} characters.`}
            error={fieldErrors['password']}
          />
          <Button type="submit" variant="primary" block loading={pending}>
            Update password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
